import addDays from 'date-fns/addDays'
import addWeeks from 'date-fns/addWeeks'
import format from 'date-fns/format'
import startOfWeek from 'date-fns/startOfWeek'
import subWeeks from 'date-fns/subWeeks'

import { AvailabilityRange } from '@/apiClient'

export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

export type EditableRange = { start: string; end: string }
export type DayRanges = EditableRange[]

export type WeekRanges = [
  DayRanges,
  DayRanges,
  DayRanges,
  DayRanges,
  DayRanges,
  DayRanges,
  DayRanges,
]

export const getWeekStart = (date: Date): Date => startOfWeek(date)

export const nextWeek = (weekStart: Date): Date => addWeeks(weekStart, 1)
export const previousWeek = (weekStart: Date): Date => subWeeks(weekStart, 1)

export const formatWeekLabel = (weekStart: Date): string => {
  const weekEnd = addDays(weekStart, 6)

  return `${format(weekStart, 'MMMM d')} – ${format(weekEnd, 'MMMM d, yyyy')}`
}

export const trimSeconds = (time: string): string => time.slice(0, 5)
export const withSeconds = (time: string): string =>
  time.length === 5 ? `${time}:00` : time

export const emptyDay = (): DayRanges => [{ start: '', end: '' }]

export const rangesToDays = (ranges: AvailabilityRange[]): WeekRanges => {
  const days: DayRanges[] = Array.from({ length: 7 }, () => [])

  for (const range of ranges) {
    days[range.weekday]?.push({
      start: trimSeconds(range.start),
      end: trimSeconds(range.end),
    })
  }

  return days.map(day => (day.length > 0 ? day : emptyDay())) as WeekRanges
}

export const daysToRanges = (days: WeekRanges): AvailabilityRange[] =>
  days.flatMap((day, weekday) =>
    day
      .filter(range => range.start && range.end)
      .map(range => ({
        weekday,
        start: withSeconds(range.start),
        end: withSeconds(range.end),
      })),
  )

export const emptyWeek = (): WeekRanges => [
  emptyDay(),
  emptyDay(),
  emptyDay(),
  emptyDay(),
  emptyDay(),
  emptyDay(),
  emptyDay(),
]

export type DayValidationError = {
  weekday: number
  rangeIndex: number
  message: string
}

const MIDNIGHT = '00:00'

const isInvalidRange = (range: EditableRange): boolean => {
  if (range.end === MIDNIGHT && range.start !== MIDNIGHT) {
    return false
  }
  return range.end <= range.start
}

export const validateWeek = (days: WeekRanges): DayValidationError[] => {
  const errors: DayValidationError[] = []

  days.forEach((day, weekday) => {
    day.forEach((range, rangeIndex) => {
      const hasStart = range.start !== ''
      const hasEnd = range.end !== ''

      if (!hasStart && !hasEnd) {
        return
      }

      if (!hasStart || !hasEnd) {
        errors.push({
          weekday,
          rangeIndex,
          message: 'Enter both a start and end time',
        })
        return
      }

      if (isInvalidRange(range)) {
        errors.push({
          weekday,
          rangeIndex,
          message: 'End time must be later than start time',
        })
      }
    })
  })

  return errors
}
